import type {
  DealHandling,
  FieldMetric,
  BuyInMetric,
  ParticipationCount,
  ParticipationMode,
  PointsDefinition,
  ReentryPolicy,
  Tiebreaker,
} from './definition';
import type { ExpressionVariable } from './expression';

/**
 * §9.5 — writing for poker room managers.
 *
 * <b>The audience runs a card room, not a database.</b> Every label an operator reads on the
 * builder is here rather than inline in a component, for two reasons: the spec's rule ("label
 * things the way they say them") is checkable in one file, and the same option means the same
 * thing wherever it appears — the radio, the read-only summary on a leaderboard, and the sentence
 * in a confirmation dialog.
 *
 * Three habits hold throughout:
 *
 * 1. **Name the thing, not the field.** "Everyone in a chop scores the lowest place", not
 *    `dealHandling: worstOfDeal`.
 * 2. **Every number gets a worked example.** A multiplier ladder means nothing until it reads
 *    "a 500-player event scores ×1.6", and those examples are computed from the operator's own
 *    figures — never invented.
 * 3. **Say the consequence.** An option that changes who wins says so.
 */

export interface Choice<T extends string> {
  value: T;
  label: string;
  /** The line under the option. Says what it does to somebody's points. */
  detail: string;
}

export const DEAL_CHOICES: readonly Choice<DealHandling>[] = [
  {
    value: 'worstOfDeal',
    label: 'Everyone scores the lowest of the chopped places',
    detail:
      'A three-way chop: all three players get 3rd-place points. This is what stops a table chopping and everybody still collecting winner’s points.',
  },
  {
    value: 'averageOfDeal',
    label: 'Everyone scores the average of the chopped places',
    detail:
      'A three-way chop worth 100, 70 and 50: all three players get 73.33. Softer than the lowest place, and what the WSOP does.',
  },
  {
    value: 'actualPlace',
    label: 'Ignore the chop — use the finishing order as published',
    detail:
      'Whoever is listed first gets winner’s points, even though the players agreed the money between them.',
  },
];

export const PARTICIPATION_CHOICES: readonly Choice<ParticipationMode>[] = [
  {
    value: 'none',
    label: 'Nothing — you have to reach the money',
    detail: 'A player who busts out before the money earns nothing for that event.',
  },
  {
    value: 'nonCashingEntriesOnly',
    label: 'Points for entries that didn’t cash',
    detail:
      'Turning up and busting is worth something. An entry that reached the money earns from the table above instead, not from both.',
  },
  {
    value: 'everyEntry',
    label: 'Points for every entry, cashed or not',
    detail:
      'On top of whatever the finish itself earned. Rewards volume hardest — a regular who plays everything gains most.',
  },
];

/** §3.7. How a player's buy-ins are counted. Every buy-in first: it is what every system did before. */
export const PARTICIPATION_COUNT_CHOICES: readonly Choice<ParticipationCount>[] = [
  {
    value: 'each',
    label: 'Every buy-in earns points',
    detail: 'A player who buys in three times and busts earns the points three times.',
  },
  {
    value: 'oncePerEvent',
    label: 'Once per tournament, however many buy-ins',
    detail:
      'A player who buys in three times and busts earns the points once. Rebuys do not add more.',
  },
];

/**
 * The busted-bullet switch's sentence, in the count's own terms: counted once, busted bullets earn
 * the tournament's value once rather than once each, and the sentence must not promise more.
 */
export function bustedBulletsDetail(count: ParticipationCount): string {
  return count === 'oncePerEvent'
    ? 'A player fires three bullets and cashes on the third: with this on, the busted bullets earn the tournament’s points once, on top of the cash. Off, only the cash counts.'
    : 'A player fires three bullets and cashes on the third: with this on, the two that busted earn as well. Off, they earn nothing and only the cash counts.';
}

/** What the value box is worth, in the count's own terms — a buy-in, or the whole tournament. */
export function participationValueLabel(count: ParticipationCount): string {
  return count === 'oncePerEvent' ? 'Points per tournament' : 'Points per buy-in';
}

/**
 * §9.5's worked example for one multiplier switch, computed from the operator's own value and
 * their own biggest band rather than from a number invented here. A system without that
 * multiplier is told the switch changes nothing, rather than shown an example of a weighting it
 * does not have.
 */
export function participationMultiplierExample(
  definition: PointsDefinition,
  which: 'field' | 'buyIn',
): string {
  const participation = definition.participation;
  const per = participation.pointsPerEntry ?? 0;
  const unit = participation.countEntries === 'oncePerEvent' ? 'tournament' : 'buy-in';

  if (which === 'field') {
    const top = definition.fieldMultiplier?.bands.at(-1)?.value;
    if (top === undefined || top === null) {
      return 'This system has no field-size weighting, so this changes nothing.';
    }
    return participation.applyFieldMultiplier
      ? `On: a ${unit} in your biggest field band is worth ${round(per * top)} rather than ${per}.`
      : `Off: a ${unit} is worth ${per} however big the field.`;
  }

  const top = definition.buyInMultiplier?.bands.at(-1)?.value;
  if (top === undefined || top === null) {
    // A v2 tag override replaces the buy-in multiplier, so it is what this switch reaches when
    // there is no band. Its value is not the designer's to read (the block is carried verbatim).
    return hasTagOverrides(definition)
      ? participation.applyBuyInMultiplier
        ? `On: a ${unit} in a tagged event takes that event’s multiplier, such as the Main Event’s.`
        : `Off: a ${unit} is worth ${per} whatever the event’s tag multiplier.`
      : 'This system has no buy-in weighting, so this changes nothing.';
  }
  return participation.applyBuyInMultiplier
    ? `On: a ${unit} in your top buy-in band is worth ${round(per * top)} rather than ${per}.`
    : `Off: a ${unit} is worth ${per} whatever it cost to enter.`;
}

function hasTagOverrides(definition: PointsDefinition): boolean {
  return Array.isArray(definition.multiplierOverrides) && definition.multiplierOverrides.length > 0;
}

function round(value: number): number {
  return Math.round(value * 100) / 100;
}

export const REENTRY_CHOICES: readonly Choice<ReentryPolicy>[] = [
  {
    value: 'bestResultOnly',
    label: 'A player scores once per event, for their best finish',
    detail: 'The ordinary answer, and the only one today’s results can tell apart.',
  },
  {
    value: 'everyEntryScores',
    label: 'Every entry scores on its own',
    detail:
      'For formats where one player can be paid twice in one event. Nothing recorded here does that yet, so today this scores identically to the option above.',
  },
];

export const FIELD_METRIC_CHOICES: readonly Choice<FieldMetric>[] = [
  {
    value: 'entries',
    label: 'Entries',
    detail: 'Every bullet fired, so a 400-runner event with re-entries counts higher than 400.',
  },
  {
    value: 'uniquePlayers',
    label: 'Players',
    detail: 'Each person once, however many times they re-entered.',
  },
  {
    value: 'cashers',
    label: 'Paid places',
    detail: 'How deep the money went, rather than how many turned up.',
  },
];

export const BUY_IN_METRIC_CHOICES: readonly Choice<BuyInMetric>[] = [
  {
    value: 'buyIn',
    label: 'Buy-in',
    detail: 'What it cost to enter, so a High Roller outweighs a nightly.',
  },
  {
    value: 'prizePool',
    label: 'Prize pool',
    detail: 'What was actually on the table — buy-in and field size together.',
  },
];

export const TIEBREAKER_LABELS: Record<Tiebreaker, string> = {
  mostFirstPlaces: 'Most wins',
  mostCashes: 'Most cashes',
  bestSingleFinish: 'Best single finish',
  mostEventsEntered: 'Most events played',
  earliestAchieved: 'Whoever got there first',
  alphabeticalByLastName: 'Last name, A to Z',
};

export const TIEBREAKER_DETAILS: Record<Tiebreaker, string> = {
  mostFirstPlaces: 'The player who won more events finishes above.',
  mostCashes: 'The player who reached the money more often finishes above.',
  bestSingleFinish: 'One deep run beats a season of min-cashes.',
  mostEventsEntered: 'The player who showed up more often finishes above.',
  earliestAchieved: 'Whoever reached the total first keeps the higher place.',
  alphabeticalByLastName: 'Always settles it, so it belongs at the bottom of the list.',
};

/**
 * §9.4's variable palette — one line per §4.3 variable, said the way a card room says it.
 *
 * These are the labels on nine clickable chips, so each one has to answer "what would I get if I
 * put this in my formula?" on its own. Two pairs are near-synonyms and the difference between them
 * is the whole reason a formula can be wrong: `place` against `effectivePlace` is what a chop does,
 * and `fieldSize` against `uniquePlayers` is what re-entries do. Both say so outright.
 */
export const EXPRESSION_VARIABLE_DETAILS: Record<ExpressionVariable, string> = {
  place: 'Where the player finished, as published.',
  effectivePlace:
    'The place after the chop rule is applied. This is usually the one you want — using “place” instead pays a chopped winner as a winner.',
  fieldSize: 'Total entries, counting every re-entry.',
  uniquePlayers: 'How many different people played, however many times each re-entered.',
  cashers: 'How many places were paid.',
  buyIn: 'What it cost to enter, in dollars.',
  prizePool: 'The whole prize pool, in dollars.',
  prizeWon: 'What this player was paid, in dollars.',
  isDeal: '1 if the event ended in a chop, 0 if it did not.',
};

/**
 * §4.3's functions, grouped the way somebody looks for one. Shown under the palette rather than as
 * chips: an operator reaches for a *value* by name and for a function by what it does.
 */
export const EXPRESSION_FUNCTION_HELP: readonly { label: string; detail: string }[] = [
  { label: 'min, max, clamp', detail: 'Keep a number inside a range — clamp(buyIn, 400, 20000).' },
  { label: 'round, floor, ceil, abs', detail: 'Tidy a number up. round takes an optional second value for decimal places.' },
  { label: 'sqrt, root, pow', detail: 'Flatten or steepen a curve — root(x, 4.5) is the 4.5th root of x.' },
  { label: 'ln, log10, log', detail: 'Diminishing returns. ln(fieldSize / place) is the usual finishing factor.' },
  { label: '+ − × ÷ % ( )', detail: 'The ordinary arithmetic, with the ordinary precedence.' },
];

/** The unit a multiplier metric is counted in, for worked examples. */
export const METRIC_UNIT: Record<FieldMetric | BuyInMetric, string> = {
  entries: 'entries',
  uniquePlayers: 'players',
  cashers: 'paid places',
  buyIn: 'buy-in',
  prizePool: 'prize pool',
};

/**
 * "A 500-player event scores ×1.6" — the sentence §9.5 asks for under every numeric input.
 *
 * The figure comes from the operator's own bands, so it is a reading of what they have built
 * rather than a claim about what any room pays.
 */
export function multiplierExample(
  metric: FieldMetric | BuyInMetric,
  sample: number,
  value: number,
): string {
  const subject =
    metric === 'buyIn'
      ? `A $${sample.toLocaleString('en-US')} event`
      : metric === 'prizePool'
        ? `An event with a $${sample.toLocaleString('en-US')} prize pool`
        : metric === 'cashers'
          ? `An event paying ${sample.toLocaleString('en-US')} places`
          : `A ${sample.toLocaleString('en-US')}-${metric === 'entries' ? 'entry' : 'player'} event`;

  return `${subject} scores ×${trimNumber(value)}.`;
}

/** Drops the trailing zeroes a fixed-precision multiplier picks up: 1.60 reads as 1.6. */
export function trimNumber(value: number): string {
  return String(Math.round(value * 10000) / 10000);
}

/**
 * What the footer says about unsaved work — and, when the test scope is the only thing that moved,
 * that the definition itself has not.
 *
 * <b>The test scope is saved work without being part of the system (§2.1, §9.8).</b> It lives on
 * the version, so leaving without saving really does lose it, and the footer has to say so. But
 * "Unsaved changes" alone, appearing the moment somebody drags a date to see a different fortnight,
 * says the opposite of what every other line on the preview pane says: that testing changes
 * nothing about what is being built. Two states, told apart.
 */
export function unsavedWorkLabel(changed: {
  definition: boolean;
  scope: boolean;
}): { label: string; detail: string | null } | null {
  if (changed.definition) return { label: 'Unsaved changes', detail: null };
  if (changed.scope) {
    return {
      label: 'Unsaved test scope',
      detail: 'The definition itself is unchanged.',
    };
  }
  return null;
}

// ---------------------------------------------------------------- §9.9, leaderboard setup

/**
 * The recalculation trigger (§9.9). What happens with nobody pressing anything — a manual
 * recalculation is available whatever this says, which is why none of these is worded as "off".
 */
export const RECALC_CHOICES: readonly Choice<'manual' | 'on_result_entry' | 'scheduled'>[] = [
  {
    value: 'manual',
    label: 'Only when I press Recalculate',
    detail:
      'Standings stay exactly as they are until you say otherwise. Safest while a series is being paid out, and the reason the Recalculate button is always there.',
  },
  {
    value: 'on_result_entry',
    label: 'Whenever a tournament that could affect it changes',
    detail:
      'Enter or correct a night’s results, delete an event, or change its date, tags or buy-in, and the board is up to date before you leave the screen. Only a change to an event this board counts — before or after the change — triggers it, so a side event nobody counts changes nothing. Editing a player’s name or location waits for the next recalculation.',
  },
  {
    value: 'scheduled',
    label: 'On a schedule',
    detail:
      'For a board a room wants refreshed at a fixed time — overnight, say — whether or not anyone touched it that day.',
  },
];

/**
 * §9.9's lock, in words. **Do not disable the control silently** — the section shows the pinned
 * version and this sentence in place of the picker, because a greyed-out box says "not now" where
 * the truth is "never, and here is why".
 */
export const PINNED_EXPLANATION =
  'Pinned when this leaderboard started. Changing the system would rewrite published standings.';

/**
 * The warning on a Running board's scope (§9.9). Scope stays editable — a late-added event is a
 * real thing and an operator must be able to add it — but it moves standings that have already
 * been published, so it says so, and names what happens next.
 */
export const RUNNING_SCOPE_WARNING =
  'This leaderboard is running, so changing which events it scores changes standings players have already seen. Recalculate afterwards to publish the new figures.';

/** §9.9's refusal to start without a system, said before the button is pressed rather than after. */
export const START_NEEDS_SYSTEM =
  'Pick a points system first. Starting is what pins the version this leaderboard scores with, and that pin never changes.';

/**
 * One line summarising a scope, for a collapsed section: "12 events · Jul 28 – Aug 11".
 *
 * `events` is null while the count is being fetched or could not be — never 0, which would say
 * the scope matched nothing when what happened is that nobody has asked yet.
 */
export function scopeSummary(
  scope: { startDate: string | null; endDate: string | null },
  events: number | null,
): string {
  const range = scopeRangeLabel(scope);
  if (events === null) return range;
  return `${events} event${events === 1 ? '' : 's'} · ${range}`;
}

/**
 * One scope's date range in words — <b>four arrangements, because an open bound is a different
 * statement from a date</b> (ratings §4.2).
 *
 * Interpolating a null straight into `… – …` gives `null – null`, and routing it through a date
 * formatter gives `Invalid Date – Invalid Date`. Both are on the collapsed summary of §5.2's
 * reference board, whose bounds are deliberately open, and both read as a broken screen rather than
 * as an unbounded scope — which is the one thing this line exists to say.
 *
 * A malformed bound falls through to the raw text rather than to `Invalid Date`: it cannot be
 * saved, but it is on screen while somebody is typing, and hiding what they typed removes the only
 * place they could see the typo.
 */
export function scopeRangeLabel(scope: { startDate: string | null; endDate: string | null }): string {
  const { startDate, endDate } = scope;

  if (startDate === null && endDate === null) return 'Every event, no date limit';
  if (endDate === null) return `${shortDate(startDate!)} onwards`;
  if (startDate === null) return `Up to ${shortDate(endDate)}`;

  return `${shortDate(startDate)} – ${shortDate(endDate)}`;
}

/** `2026-08-11` → `Aug 11`. Falls back to the raw value rather than showing `Invalid Date`. */
function shortDate(iso: string): string {
  const parsed = new Date(`${iso}T00:00:00`);
  if (Number.isNaN(parsed.getTime())) return iso;
  return parsed.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}
